import type { NextPage } from 'next'
import { useRouter } from 'next/router'
import { useEffect } from 'react'

import { useUser } from 'src/providers/UserProvider'

import { Card } from 'src/components/atoms/Card'
import {
  Heading3,
  Paragraph
} from 'src/components/atoms/DesignSystem/Typography/Typography'
import { ExternalLink } from 'src/components/atoms/Link'
import { LoadingSpinner } from 'src/components/atoms/LoadingSpinner'
import { Show } from 'src/components/atoms/Show'
import { Heading1, Intro } from 'src/components/atoms/Typography'
import {
  FullWidthContainer,
  FullWidthContainerBackgroundColor
} from 'src/components/layouts/Container'
import { Page } from 'src/components/layouts/Page'
import { Tabs } from 'src/components/molecules/Tabs'
import { TabData } from 'src/components/molecules/Tabs/Tabs'
import { ResourceGrid } from 'src/components/organisms/ResourceGrid'

import { useGetApplicationCountForPlanterRegistrationQuery } from 'src/config/generated/graphql'
import { ApplicationWorkflowState } from 'src/types/application'
import { withAuthRequired } from 'src/utils/auth'

enum PlanterToolkitTabs {
  Resources = 'Resources',
  Videos = 'Videos'
}

const PlanterToolkit: NextPage = () => {
  const router = useRouter()

  const { activeTab = PlanterToolkitTabs.Resources } = router.query as {
    activeTab: PlanterToolkitTabs
  }

  const tabConfig: TabData[] = [
    {
      label: 'Downloads',
      value: PlanterToolkitTabs.Resources
    },
    {
      label: 'Videos',
      value: PlanterToolkitTabs.Videos
    }
  ]

  // Does the planter have an approved application
  const { user } = useUser()
  const { data: applicationCountData, loading } =
    useGetApplicationCountForPlanterRegistrationQuery({
      variables: {
        workflowStates: [
          ApplicationWorkflowState.Approved,
          ApplicationWorkflowState.Planted,
          ApplicationWorkflowState.Monitoring
        ],
        planterId: user?.Account?.PlanterId
      }
    })

  const approvedApplicationCount =
    applicationCountData?.Application_aggregate?.aggregate?.count
  const resourcesEnabled =
    approvedApplicationCount && approvedApplicationCount > 0

  useEffect(() => {
    const hash = router.asPath.split('#')[1]

    if (hash && activeTab === PlanterToolkitTabs.Videos) {
      let timeoutId: NodeJS.Timeout | null = null
      let attempts = 0
      const maxAttempts = 10

      const scrollToElement = () => {
        const element = document.getElementById(hash)
        if (element) {
          const offset = 100
          const elementPosition = element.getBoundingClientRect().top
          const offsetPosition = elementPosition + window.pageYOffset - offset

          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          })
        } else if (attempts < maxAttempts) {
          attempts += 1
          timeoutId = setTimeout(scrollToElement, 100)
        }
      }

      timeoutId = setTimeout(scrollToElement, 100)

      return () => {
        if (timeoutId) clearTimeout(timeoutId)
      }
    }
  }, [router.asPath, activeTab])

  const handleChangeTab = (newValue: string) => {
    router.replace(
      { query: { ...router.query, activeTab: newValue } },
      undefined,
      { scroll: false }
    )
  }

  return (
    <Page title='Planter toolkit' isFullWidth>
      <FullWidthContainer
        backgroundColor={FullWidthContainerBackgroundColor.linen}
      >
        <Heading1>Planter toolkit</Heading1>
        <Intro>
          Get started with these helpful guides and videos, put together by our
          team of Technical Advisors to help improve your planting projects.
        </Intro>
        <Card className='mt-8'>
          <Tabs
            className='mb-5 md:mb-10'
            tabs={tabConfig}
            activeTab={activeTab}
            ariaLabel='Tab'
            onChange={handleChangeTab}
          />
          <Show when={activeTab === PlanterToolkitTabs.Resources}>
            <Heading3 className='pb-4'>Downloadable resources</Heading3>
            <Intro className='pb-8'>
              Been approved to receive native trees for your project? Download
              our resources to help with planning, planting, and monitoring.
            </Intro>
            <LoadingSpinner isLoading={loading}>
              <ResourceGrid
                audience='planters'
                resourceTypes={['view', 'download']}
                disabled={!resourcesEnabled}
              />
            </LoadingSpinner>
          </Show>
          <Show when={activeTab === PlanterToolkitTabs.Videos}>
            <Heading3 className='pb-4'>Video resources</Heading3>
            <Intro className='pb-8'>
              Watch our videos to help with planning, planting, and monitoring,
              ensuring successful projects and a greener Aotearoa.
            </Intro>
            <ResourceGrid audience='planters' resourceTypes={['video']} />
          </Show>
        </Card>
        <Card className='mt-8'>
          <Heading3 className='pb-4'>Need a hand?</Heading3>
          <Paragraph>
            To learn more about planting with Trees That Count{' '}
            <ExternalLink href='https://help.treesthatcount.co.nz'>
              within our help centre
            </ExternalLink>{' '}
            or reach out to our team planter@treesthatcount.co.nz.
          </Paragraph>
        </Card>
      </FullWidthContainer>
    </Page>
  )
}

export default withAuthRequired(PlanterToolkit)
